import {inject, Injectable, OnDestroy} from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import {catchError, EMPTY, filter, finalize, Observable, Subject, throwError} from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HTTP_CODES } from "../interfaces/http-codes";
import {LoaderService, SKIP_LOADER} from "./loader.service";
import {Router} from "@angular/router";
import {ErrorHandlerService} from "./error-handler.service";
import {LocalStorageService} from "./local-storage.service";
import {HttpErrorNotifyService} from "./http-error-notify.service";

/** Ключ у sessionStorage: коли востаннє інтерцептор сам перезавантажував сторінку */
const RELOAD_MARK_KEY = 'http_interceptor_reload_at';
/** Не перезавантажуємо сторінку частіше, ніж раз на хвилину - інакше цикл перезавантажень */
const RELOAD_COOLDOWN_MS = 60000;
/** Ендпоінт відправки логу помилки (ErrorHandlerService) - його не можна обробляти як звичайний запит */
const ERROR_REPORT_PATH = 'header/error_handler';

/** Запит сам розбирає коди редиректу (not_authorized, user_deleted, site_blocked) - відповідь з ними
 *  йому віддаємо, як і раніше. Потрібно оформленню замовлення (pay): запит без відповіді воно
 *  вважає «статус невідомий», а на ці коди бек відмовляє ще до створення замовлення. */
export const PASS_REDIRECT_RESPONSE = new HttpContextToken<boolean>(() => false);

/** Запит сам показує причину відмови 4xx (чат помічника: у тілі 403/429 бек кладе text для вікна чату) -
 *  загальний тост «Запит відхилено сервером» для нього не показуємо. Гілки 404, 410, 413, 5xx, «немає
 *  зв'язку» з їхніми тостами, звітами й перезавантаженням - як у всіх запитів. */
export const OWN_CLIENT_ERROR_TEXT = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements  HttpInterceptor, OnDestroy {
  public loader = inject(LoaderService);
  private router = inject(Router);
  private errorHandlerService = inject(ErrorHandlerService);
  private localStorage = inject(LocalStorageService);
  private notify = inject(HttpErrorNotifyService);

  private requestsCount = 0;
  /** Перезавантаження вже запущено цим завантаженням сторінки */
  private reloadScheduled = false;

  public isHidden: boolean;
  public isDestroyed: Subject<boolean> = new Subject();

  constructor(
  ) {
    this.loader.hiddenChange
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((value) => {
        this.isHidden = value;
      });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Відправка логу помилки йде фоном: вона не повинна ні крутити повноекранний лоадер,
    // ні потрапляти у власну обробку помилок (інакше невдалий лог породжує новий лог).
    if (req.url.indexOf(ERROR_REPORT_PATH) !== -1) {
      return next.handle(req);
    }

    // Фоновий запит лише не рахується в лоадері; решта обробки (помилки, редиректи, скасування) - як у всіх
    const withLoader = !req.context.get(SKIP_LOADER);

    if (withLoader) {
      this.loader.hiddenChange.next(false);
      ++this.requestsCount;

      if (this.loader.clearRequest) {
        this.loader.clearRequest = false;
        this.requestsCount = 0;
      }
    }

    return next.handle(req)
      .pipe(
        filter((event: HttpEvent<any>) => {
          if (!(event instanceof HttpResponse) || !event.body) {
            return true;
          }

          if (event.body.error === 'not_authorized') {
            // Сторінка входу тепер на новому фронтенді (v2)
            window.location.assign('/login');
          } else if (event.body.error === 'user_deleted') {
            this.router.navigate(['deleted']);

            this.loader.hiddenChange.next(true);
          } else if (event.body.error === 'site_blocked') {
            this.router.navigate(['site_blocked']);

            this.loader.hiddenChange.next(true);
          } else {
            return true;
          }

          // Після редиректу відповідь компонентам не віддаємо: у тілі лише код помилки, а сторінка
          // розбирала її як дані (TypeError у лог, тости, «збережено»). Запит завершується
          // без відповіді - як при скасуванні лоадером.
          return req.context.get(PASS_REDIRECT_RESPONSE);
        }),
        takeUntil(this.loader.onCancelPendingRequests()),
        catchError(response => {
          if (response instanceof HttpErrorResponse) {
            return this.handleHttpError(response, req);
          }

          // Сюди потрапляє не HTTP-помилка, а збій усередині самого пайпа - це вже наш баг,
          // про нього повідомляємо на сервер (раніше так само репортилась кожна звичайна 404).
          this.errorHandlerService.handleError(
            response instanceof Error ? response : new Error('Request pipeline error: ' + String(response))
          );

          return throwError(() => response);
        }),
        // Счётчик уменьшается именно здесь: finalize срабатывает и на успешном ответе,
        // и на ошибке, и на отмене (takeUntil/cancelPendingRequests), и при отписке —
        // например, когда пользователь уходит со страницы и компонент уничтожается.
        // Иначе счётчик залипал и полноэкранный лоадер больше не скрывался.
        finalize(() => {
          if (withLoader) {
            this.releaseRequest();
          }
        })
      );
  }

  /**
   * Картка #252: розбір HTTP-помилки. Для кожної причини - свій текст користувачу,
   * на сервер повідомляємо лише про те, що справді зламане (раніше туди летіла
   * кожна помилка разом із повним дампом відповіді), а помилку прокидаємо далі,
   * щоб компонент міг доробити власний сценарій.
   */
  private handleHttpError(response: HttpErrorResponse, req: HttpRequest<any>): Observable<never> {
    // Відповіді не було: немає мережі або запит обірвано. Слати лог на той самий
    // недоступний бек немає сенсу.
    if (response.status === HTTP_CODES.NO_RESPONSE) {
      this.notify.show(
        'Немає зв\'язку з сервером',
        'Перевірте інтернет-з\'єднання та повторіть дію.'
      );

      return throwError(() => response);
    }

    // Статус 200, але тіло не розібралось як JSON: бек відповів редиректом/HTML замість JSON.
    // Відомий випадок - обрана доставка стала недоступною, бек уводить запит на іншу сторінку.
    if (response.status === HTTP_CODES.SUCCESS) {
      if (this.localStorage.get('delivery_id') !== null && this.canReload()) {
        this.localStorage.delete('delivery_id');
        this.reloadNow();

        return EMPTY;
      }

      this.errorHandlerService.handleError(new Error('Non-JSON response: ' + this.describe(response)));
      this.notify.show(
        'Сервер повернув некоректну відповідь',
        'Оновіть сторінку. Якщо помилка повториться - зверніться до менеджера.'
      );

      return throwError(() => response);
    }

    // web_api віддає 404, коли метод не знайдено, і сам пише про це в лог (WEB_API_404),
    // тому власний звіт тут був би дублем.
    if (response.status === HTTP_CODES.NOT_FOUND) {
      this.notify.show(
        'Операція недоступна',
        'Сервер не знайшов запитану операцію. Можливо, вкладка відкрита давно - оновіть сторінку.'
      );

      return throwError(() => response);
    }

    // 410 приходить, коли зібрана версія застаріла. Раніше тут стояв безумовний
    // location.reload(): якщо 410 повторювався, сторінка перезавантажувалась по колу.
    if (response.status === HTTP_CODES.GONE) {
      if (this.canReload()) {
        this.reloadNow();

        return EMPTY;
      }

      this.notify.show(
        'Версія сторінки застаріла',
        'Оновіть сторінку, щоб продовжити роботу.'
      );

      return throwError(() => response);
    }

    // 413 віддає nginx на завеликому файлі (імпорт кошика). Текст від nginx - HTML,
    // показувати його користувачу не можна.
    if (response.status === HTTP_CODES.PAYLOAD_TOO_LARGE) {
      this.notify.show(
        'Файл завеликий',
        'Сервер не прийняв файл через його розмір. Зменшіть файл і спробуйте ще раз.'
      );

      return throwError(() => response);
    }

    if (response.status >= HTTP_CODES.SERVER_ERROR) {
      this.errorHandlerService.handleError(new Error('Server error: ' + this.describe(response)));
      this.notify.show(
        'Сервер тимчасово недоступний',
        'Спробуйте повторити дію за кілька хвилин.'
      );

      return throwError(() => response);
    }

    if (response.status >= HTTP_CODES.CLIENT_ERROR) {
      if (!req.context.get(OWN_CLIENT_ERROR_TEXT)) {
        this.notify.show(
          'Запит відхилено сервером',
          'Оновіть сторінку і спробуйте ще раз. Якщо помилка повториться - зверніться до менеджера.'
        );
      }

      return throwError(() => response);
    }

    // Нестандартний статус (1xx/3xx у відповіді HttpClient) - самі не знаємо причини, репортимо.
    this.errorHandlerService.handleError(new Error('Unexpected response: ' + this.describe(response)));
    this.notify.show(
      'Запит не виконано',
      'Спробуйте ще раз. Якщо помилка повториться - зверніться до менеджера.'
    );

    return throwError(() => response);
  }

  /** Короткий опис відповіді для логу - без тіла, щоб не тягнути в лог дані користувача */
  private describe(response: HttpErrorResponse): string {
    return (response.status + ' ' + (response.statusText || '') + ' ' + (response.url || '')).trim();
  }

  /**
   * Чи можна зараз перезавантажити сторінку. Обмеження - не частіше разу на хвилину:
   * помилка, що не зникає після перезавантаження, інакше ганяє сторінку по колу
   * і довбає бек (так поводився попередній безумовний location.reload()).
   */
  private canReload(): boolean {
    // Кілька паралельних запитів впали однаково - перезавантаження вже йде
    if (this.reloadScheduled) {
      return true;
    }

    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_MARK_KEY)) || 0;

    return Date.now() - lastReloadAt >= RELOAD_COOLDOWN_MS;
  }

  /** Перезавантажити сторінку - один раз за це її завантаження */
  private reloadNow(): void {
    if (this.reloadScheduled) {
      return;
    }

    this.reloadScheduled = true;
    sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
    location.reload();
  }

  /** Уменьшить счётчик активных запросов и при необходимости скрыть лоадер */
  private releaseRequest(): void {
    // не уходим в минус: clearRequest может обнулить счётчик, пока запросы ещё в полёте
    this.requestsCount = Math.max(0, this.requestsCount - 1);

    if (this.requestsCount <= 0) {
      this.loader.hiddenChange.next(true);
    }
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}
